/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Stack,
    Typography,
    Chip,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Alert,
} from '@mui/material';
import { Iconify } from 'src/components';
import { APPLE, appleInputSx } from 'src/components/templates/appleDesignTokens';
import {
    AUDIENCE_POLICY,
    TREATMENT_RULE_MODE,
    HEALTHCARE_PROVIDERS,
    ALL_PROVIDER_VALUES,
    toRuleValue,
    audienceSentence,
    danglingTreatmentIds,
    treatmentNameIndex,
    treatmentCountByCategory,
    validateRule,
} from './audienceRulesUtils';

// One editor, five homes: the template preview drawer, the Edit WhatsApp
// Template dialog, the bulk-edit dialog on the templates grid, and the
// practice-default card in Settings. Single-source so they cannot drift.
//
// The organising idea: radio buttons and checkboxes describe the CONTROLS; the
// summary line at the top describes the CONSEQUENCE. An operator should be able
// to read one sentence and know who gets the message, without decoding three
// separate widgets.

const MODE_OPTIONS = [
    { value: TREATMENT_RULE_MODE.ALL, label: 'All' },
    { value: TREATMENT_RULE_MODE.ALLOW, label: 'Only these' },
    { value: TREATMENT_RULE_MODE.BLOCK, label: 'All except these' },
];

// Extracted so the chip JSX stays flat — a nested ternary inline would trip the
// house lint rule and read worse besides.
const providerChipTooltip = (label, on, locked) => {
    if (locked) return 'At least one patient type must stay on';
    return on
        ? `Switch off to stop sending to ${label} patients`
        : `Switch on to send to ${label} patients`;
};

const rowLabelSx = {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: APPLE.grayText,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: APPLE.font,
};

const groupHeaderSx = {
    ...rowLabelSx,
    fontSize: '0.62rem',
    px: 1.25,
    py: 0.6,
    display: 'block',
    position: 'sticky',
    top: 0,
    bgcolor: '#fbfbfd',
    borderBottom: `1px solid ${APPLE.grayBorder}55`,
    zIndex: 1,
};

const WhatsAppAudienceRulesEditor = ({
    value,
    onChange,
    practiceTreatments = [],
    showPolicySwitch = true,
    inheritedRule = null,
    disabled = false,
    dense = false,
}) => {
    const rule = toRuleValue(value);
    const isCustom = rule.audience_policy === AUDIENCE_POLICY.CUSTOM;
    const [treatmentSearch, setTreatmentSearch] = useState('');

    const patch = (next) => {
        if (disabled) return;
        onChange({ ...rule, ...next });
    };

    // ── Treatment catalogue, flattened once ──────────────────────────────
    const { categories, items, knownCategoryIds, knownItemIds, nameIndex } = useMemo(() => {
        const catMap = new Map();
        const itemMap = new Map();
        const counts = treatmentCountByCategory(practiceTreatments);
        (practiceTreatments || []).forEach((row) => {
            const item = row?.treatment_item;
            if (!item?.id) return;
            const category = item.treatment_category;
            if (category?.id && !catMap.has(category.id)) {
                catMap.set(category.id, {
                    id: category.id,
                    name: category.name || 'Uncategorised',
                    count: counts[category.id] || 0,
                });
            }
            if (!itemMap.has(item.id)) {
                itemMap.set(item.id, {
                    id: item.id,
                    name: item.name || 'Unknown',
                    categoryId: category?.id ?? null,
                    categoryName: category?.name || '',
                });
            }
        });
        const cats = [...catMap.values()].sort((a, b) => a.name.localeCompare(b.name));
        const its = [...itemMap.values()].sort((a, b) => a.name.localeCompare(b.name));
        return {
            categories: cats,
            items: its,
            knownCategoryIds: cats.map((c) => c.id),
            knownItemIds: its.map((i) => i.id),
            nameIndex: treatmentNameIndex(practiceTreatments),
        };
    }, [practiceTreatments]);

    const search = treatmentSearch.trim().toLowerCase();
    const visibleCategories = search
        ? categories.filter((c) => c.name.toLowerCase().includes(search))
        : categories;
    const visibleItems = search
        ? items.filter((i) => i.name.toLowerCase().includes(search)
            || i.categoryName.toLowerCase().includes(search))
        : items;

    const dangling = useMemo(
        () => danglingTreatmentIds(rule, { categoryIds: knownCategoryIds, itemIds: knownItemIds }),
        [rule.treatment_rule_categories, rule.treatment_rule_items, knownCategoryIds, knownItemIds],
    );
    const danglingCount = dangling.categories.length + dangling.items.length;

    const toggleProvider = (provider) => {
        const current = rule.audience_healthcare_providers.length
            ? rule.audience_healthcare_providers
            : ALL_PROVIDER_VALUES; // empty == every provider allowed
        const next = current.includes(provider)
            ? current.filter((p) => p !== provider)
            : [...current, provider];
        // Everything selected is the same as no restriction — store [] so the
        // summary, the grid chip and the backend fast path all agree.
        const normalised = ALL_PROVIDER_VALUES.every((p) => next.includes(p)) ? [] : next;
        patch({ audience_healthcare_providers: normalised });
    };

    const toggleId = (field, id) => {
        const current = rule[field] || [];
        patch({
            [field]: current.includes(id)
                ? current.filter((v) => v !== id)
                : [...current, id],
        });
    };

    const providerIsOn = (provider) => (
        rule.audience_healthcare_providers.length === 0
        || rule.audience_healthcare_providers.includes(provider)
    );

    // Turning the last patient type off would silence the template entirely —
    // almost certainly a slip rather than an intent, so the last one on is held.
    const isLastProviderOn = (provider) => {
        const on = ALL_PROVIDER_VALUES.filter(providerIsOn);
        return on.length === 1 && on[0] === provider;
    };

    const validationError = validateRule(rule);
    const selectedCount = (rule.treatment_rule_categories?.length || 0)
        + (rule.treatment_rule_items?.length || 0);
    const effectiveForSummary = showPolicySwitch && !isCustom ? (inheritedRule || {}) : rule;

    return (
        <Box sx={{ fontFamily: APPLE.font }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.25 }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: APPLE.font }}>
                    Audience
                </Typography>

                {showPolicySwitch && (
                    <Box sx={{
                        display: 'inline-flex',
                        bgcolor: APPLE.grayBg,
                        borderRadius: '10px',
                        p: '2px',
                    }}>
                        {[
                            { key: AUDIENCE_POLICY.INHERIT, label: 'Practice default' },
                            { key: AUDIENCE_POLICY.CUSTOM, label: 'Custom' },
                        ].map((option) => {
                            const active = rule.audience_policy === option.key;
                            return (
                                <Button
                                    key={option.key}
                                    disableRipple
                                    disabled={disabled}
                                    onClick={() => patch({ audience_policy: option.key })}
                                    sx={{
                                        textTransform: 'none',
                                        fontFamily: APPLE.font,
                                        fontWeight: active ? 600 : 450,
                                        fontSize: '0.72rem',
                                        letterSpacing: '-0.01em',
                                        px: 1.5,
                                        py: 0.35,
                                        minWidth: 0,
                                        borderRadius: '8px',
                                        color: active ? 'text.primary' : APPLE.grayText,
                                        bgcolor: active ? 'background.paper' : 'transparent',
                                        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.10)' : 'none',
                                        transition: 'all .18s ease',
                                        '&:hover': { bgcolor: active ? 'background.paper' : 'rgba(0,0,0,0.04)' },
                                    }}
                                >
                                    {option.label}
                                </Button>
                            );
                        })}
                    </Box>
                )}
            </Stack>

            {/* ── The consequence, in one sentence ─────────────────────────
                Sits above the controls on purpose: it is the only thing most
                operators need to read, and it updates as they click. */}
            <Box sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                p: 1.25,
                mb: 1.75,
                borderRadius: '10px',
                bgcolor: `${APPLE.accent}0D`,
                border: `1px solid ${APPLE.accent}26`,
            }}>
                <Iconify
                    icon="mdi:account-multiple-outline"
                    width={15}
                    sx={{ color: APPLE.accent, mt: '1px', flexShrink: 0 }}
                />
                <Typography sx={{
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    color: 'text.primary',
                    fontFamily: APPLE.font,
                }}>
                    {audienceSentence(effectiveForSummary, nameIndex)}
                    {showPolicySwitch && !isCustom && (
                        <Typography component="span" sx={{ fontSize: '0.75rem', color: APPLE.grayText, fontFamily: APPLE.font }}>
                            {' '}Following the practice default — change it in Message
                            Templates, Settings.
                        </Typography>
                    )}
                </Typography>
            </Box>

            {(isCustom || !showPolicySwitch) && (
                <Stack spacing={dense ? 1.75 : 2.25}>
                    {/* ── Patient type ────────────────────────────────── */}
                    <Box>
                        <Typography sx={{ ...rowLabelSx, mb: 0.85 }}>Send to</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {HEALTHCARE_PROVIDERS.map((provider) => {
                                const on = providerIsOn(provider.value);
                                const locked = on && isLastProviderOn(provider.value);
                                return (
                                    <Tooltip
                                        key={provider.value}
                                        title={providerChipTooltip(provider.label, on, locked)}
                                    >
                                        <Box component="span">
                                            <Chip
                                                size="small"
                                                label={provider.label}
                                                disabled={disabled}
                                                onClick={() => !locked && toggleProvider(provider.value)}
                                                // Same icon footprint in both states, so the row
                                                // does not reflow as chips are toggled.
                                                icon={(
                                                    <Iconify
                                                        icon={on ? 'mdi:check-circle' : 'mdi:circle-outline'}
                                                        width={14}
                                                    />
                                                )}
                                                sx={{
                                                    cursor: disabled || locked ? 'default' : 'pointer',
                                                    height: 30,
                                                    px: 0.5,
                                                    fontSize: '0.75rem',
                                                    fontWeight: on ? 600 : 500,
                                                    fontFamily: APPLE.font,
                                                    borderRadius: '999px',
                                                    border: '1px solid transparent',
                                                    transition: 'all .18s ease',
                                                    ...(on
                                                        ? {
                                                            bgcolor: APPLE.accent,
                                                            color: '#fff',
                                                            '& .MuiChip-icon': { color: '#fff', ml: '6px' },
                                                            '&:hover': { bgcolor: locked ? APPLE.accent : APPLE.accentHover },
                                                        }
                                                        : {
                                                            bgcolor: 'rgba(120,120,128,0.10)',
                                                            color: APPLE.grayText,
                                                            '& .MuiChip-icon': { color: '#c4c4c8', ml: '6px' },
                                                            '&:hover': { bgcolor: 'rgba(120,120,128,0.18)' },
                                                        }),
                                                    ...(locked && { opacity: 0.75 }),
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    </Box>

                    {/* ── Treatments ──────────────────────────────────── */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={rowLabelSx}>Treatments</Typography>
                            {rule.treatment_rule_mode !== TREATMENT_RULE_MODE.ALL && selectedCount > 0 && (
                                <Typography sx={{ fontSize: '0.68rem', color: APPLE.accent, fontWeight: 600, fontFamily: APPLE.font }}>
                                    {selectedCount} selected
                                </Typography>
                            )}
                        </Stack>
                        <RadioGroup
                            row
                            value={rule.treatment_rule_mode}
                            onChange={(e) => patch({
                                treatment_rule_mode: e.target.value,
                                ...(e.target.value === TREATMENT_RULE_MODE.ALL
                                    ? { treatment_rule_categories: [], treatment_rule_items: [], send_when_treatment_unknown: false }
                                    : {}),
                            })}
                            sx={{ gap: 0.5, mt: 0.25 }}
                        >
                            {MODE_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    disabled={disabled}
                                    sx={{ mr: 1.5 }}
                                    control={(
                                        <Radio
                                            size="small"
                                            sx={{ p: 0.6, '&.Mui-checked': { color: APPLE.accent } }}
                                        />
                                    )}
                                    label={(
                                        <Typography sx={{ fontSize: '0.78rem', fontFamily: APPLE.font }}>
                                            {option.label}
                                        </Typography>
                                    )}
                                />
                            ))}
                        </RadioGroup>

                        {rule.treatment_rule_mode !== TREATMENT_RULE_MODE.ALL && (
                            <>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={treatmentSearch}
                                    onChange={(e) => setTreatmentSearch(e.target.value)}
                                    placeholder="Search categories and treatments"
                                    disabled={disabled}
                                    sx={{ ...appleInputSx, mb: 0.85, mt: 0.5 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify icon="eva:search-fill" width={16} sx={{ color: APPLE.grayText }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Box sx={{
                                    border: `1px solid ${APPLE.grayBorder}`,
                                    borderRadius: '10px',
                                    maxHeight: dense ? 230 : 290,
                                    overflow: 'auto',
                                    bgcolor: '#fff',
                                }}>
                                    {visibleCategories.length === 0 && visibleItems.length === 0 ? (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '0.74rem', color: APPLE.grayText, fontFamily: APPLE.font }}>
                                                {search
                                                    ? `Nothing matches “${treatmentSearch.trim()}”.`
                                                    : 'No treatments set up for this practice yet.'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <List dense disablePadding>
                                            {visibleCategories.length > 0 && (
                                                <Typography sx={groupHeaderSx}>
                                                    Categories
                                                </Typography>
                                            )}
                                            {visibleCategories.map((category) => {
                                                const checked = rule.treatment_rule_categories.includes(category.id);
                                                return (
                                                    <ListItemButton
                                                        key={`c${category.id}`}
                                                        dense
                                                        disabled={disabled}
                                                        onClick={() => toggleId('treatment_rule_categories', category.id)}
                                                        sx={{ px: 1.25, py: 0.4, borderRadius: '7px', mx: 0.5, my: '1px' }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 26 }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={checked}
                                                                sx={{ p: 0, color: '#c4c4c8', '&.Mui-checked': { color: APPLE.accent } }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={category.name}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.79rem',
                                                                fontFamily: APPLE.font,
                                                                fontWeight: checked ? 600 : 450,
                                                            }}
                                                        />
                                                        {/* Counts make the blast radius of picking a
                                                            category obvious before you pick it. */}
                                                        <Typography sx={{
                                                            fontSize: '0.68rem',
                                                            color: checked ? APPLE.accent : APPLE.grayText,
                                                            fontFamily: APPLE.font,
                                                            fontWeight: checked ? 600 : 400,
                                                            flexShrink: 0,
                                                            ml: 1,
                                                        }}>
                                                            {category.count} treatment{category.count === 1 ? '' : 's'}
                                                        </Typography>
                                                    </ListItemButton>
                                                );
                                            })}

                                            {visibleItems.length > 0 && (
                                                <Typography sx={groupHeaderSx}>
                                                    Individual treatments
                                                </Typography>
                                            )}
                                            {visibleItems.map((item) => {
                                                const checked = rule.treatment_rule_items.includes(item.id);
                                                // Its category is already selected, so ticking the
                                                // item adds nothing — say so rather than letting
                                                // someone wonder why it looks unselected.
                                                const coveredByCategory = item.categoryId != null
                                                    && rule.treatment_rule_categories.includes(item.categoryId);
                                                return (
                                                    <ListItemButton
                                                        key={`i${item.id}`}
                                                        dense
                                                        disabled={disabled || coveredByCategory}
                                                        onClick={() => toggleId('treatment_rule_items', item.id)}
                                                        sx={{
                                                            px: 1.25,
                                                            py: 0.4,
                                                            borderRadius: '7px',
                                                            mx: 0.5,
                                                            my: '1px',
                                                            ...(coveredByCategory && { opacity: 0.55 }),
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 26 }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={checked || coveredByCategory}
                                                                sx={{ p: 0, color: '#c4c4c8', '&.Mui-checked': { color: APPLE.accent } }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={item.name}
                                                            secondary={coveredByCategory
                                                                ? `Covered by ${item.categoryName}`
                                                                : item.categoryName}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.79rem',
                                                                fontFamily: APPLE.font,
                                                                fontWeight: checked ? 600 : 450,
                                                            }}
                                                            secondaryTypographyProps={{
                                                                fontSize: '0.66rem',
                                                                fontStyle: coveredByCategory ? 'italic' : 'normal',
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </List>
                                    )}
                                </Box>

                                {rule.treatment_rule_mode === TREATMENT_RULE_MODE.ALLOW && (
                                    <FormControlLabel
                                        sx={{ mt: 0.75, ml: 0, alignItems: 'center' }}
                                        disabled={disabled}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={rule.send_when_treatment_unknown}
                                                onChange={(e) => patch({ send_when_treatment_unknown: e.target.checked })}
                                                sx={{ p: 0.6, color: '#c4c4c8', '&.Mui-checked': { color: APPLE.accent } }}
                                            />
                                        )}
                                        label={(
                                            <Typography sx={{ fontSize: '0.75rem', fontFamily: APPLE.font }}>
                                                Also send when no treatment is recorded
                                            </Typography>
                                        )}
                                    />
                                )}
                            </>
                        )}
                    </Box>

                    {danglingCount > 0 && (
                        <Alert severity="warning" sx={{ borderRadius: '10px', fontSize: '0.72rem', py: 0.25 }}>
                            {danglingCount} selected treatment{danglingCount === 1 ? '' : 's'} no
                            longer exist{danglingCount === 1 ? 's' : ''} in this practice.
                            {rule.treatment_rule_mode === TREATMENT_RULE_MODE.ALLOW
                                && ' A list made only of removed treatments never matches anyone.'}
                        </Alert>
                    )}

                    {validationError && (
                        <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.72rem', py: 0.25 }}>
                            {validationError}
                        </Alert>
                    )}
                </Stack>
            )}
        </Box>
    );
};

WhatsAppAudienceRulesEditor.propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    practiceTreatments: PropTypes.array,
    showPolicySwitch: PropTypes.bool,
    inheritedRule: PropTypes.object,
    disabled: PropTypes.bool,
    dense: PropTypes.bool,
};

export default WhatsAppAudienceRulesEditor;
